/**
 * N타입 - 숫자 입력 컴포넌트
 * 정수/소수 입력
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';
import { PixelInput } from '../common/PixelInput';

/**
 * NumberInput Props
 */
interface NumberInputProps {
  /** 답안 제출 콜백 */
  onSubmit: (answer: string) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * N타입 숫자 입력 컴포넌트
 *
 * - 숫자 키패드 (0-9, 소수점, 음수)
 * - LCD 스타일 디스플레이
 * - 백스페이스, 클리어 버튼
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  onSubmit,
  disabled = false,
}) => {
  const [value, setValue] = useState('');

  /**
   * 숫자 버튼 클릭 핸들러
   */
  const handleNumberClick = (num: string) => {
    if (disabled) return;

    // 소수점 중복 방지
    if (num === '.' && value.includes('.')) return;

    // 음수 부호는 맨 앞에만
    if (num === '-') {
      if (value.startsWith('-')) {
        setValue(value.substring(1));
      } else {
        setValue('-' + value);
      }
      return;
    }

    setValue((prev) => prev + num);
  };

  /**
   * 백스페이스 핸들러
   */
  const handleBackspace = () => {
    if (disabled) return;
    setValue((prev) => prev.slice(0, -1));
  };

  /**
   * 클리어 핸들러
   */
  const handleClear = () => {
    if (disabled) return;
    setValue('');
  };

  /**
   * 제출 핸들러
   */
  const handleSubmit = () => {
    if (!value || disabled) return;
    onSubmit(value);
    // 제출 후 초기화
    setValue('');
  };

  const numberButtons = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '±', '0', '.'];

  return (
    <div className="space-y-1">
      {/* LCD 디스플레이 (초컴팩트) */}
      <div
        className="bg-[#9bbc0f] p-1 rounded-sm
                   border border-[#0f380f]"
      >
        <div className="bg-[#8bac0f] px-2 py-1 text-right font-pixel text-lg text-[#0f380f] min-h-[32px] flex items-center justify-end">
          {value || '0'}
        </div>
      </div>

      {/* 숫자 키패드 (초컴팩트) */}
      <div className="grid grid-cols-3 gap-1">
        {numberButtons.map((num) => (
          <PixelButton
            key={num}
            variant="secondary"
            size="small"
            onClick={() => handleNumberClick(num === '±' ? '-' : num)}
            disabled={disabled}
            className="text-sm py-1 min-h-0"
          >
            {num}
          </PixelButton>
        ))}
      </div>

      {/* 제출 버튼 (제어 버튼 통합) */}
      <div className="grid grid-cols-3 gap-1">
        <PixelButton
          variant="secondary"
          size="small"
          onClick={handleBackspace}
          disabled={disabled || !value}
          className="text-xs py-1"
        >
          ←
        </PixelButton>
        <PixelButton
          variant="primary"
          size="small"
          onClick={handleSubmit}
          disabled={!value || disabled}
          className="text-xs py-1"
        >
          제출
        </PixelButton>
        <PixelButton
          variant="secondary"
          size="small"
          onClick={handleClear}
          disabled={disabled || !value}
          className="text-xs py-1"
        >
          C
        </PixelButton>
      </div>
    </div>
  );
};
